/**
 * useExtractionViewerData
 *
 * Hook for fetching extraction metadata and sales order draft data
 * for the extraction viewer page.
 */

import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  GetExtractionMetadataResponse,
  ExtractionDocument,
  ExtractionOutput,
} from '@erp/rpc';
import config from '@/config';
import type { SalesOrderDraft } from '@/types/application/SalesOrderDraftDTO';

interface UseExtractionViewerDataParams {
  /** The staged purchase order / sales order draft ID */
  salesOrderDraftId: string;
  /** Enable/disable the queries */
  enabled?: boolean;
}

interface UseExtractionViewerDataReturn {
  /** The source document info (URL, mime type, etc.) */
  document: ExtractionDocument | null;
  /** The extraction output with metadata and citations */
  extraction: ExtractionOutput | null;
  /** Which purchase order index in multi-PO documents */
  purchaseOrderIndex: number;
  /** The sales order draft data */
  salesOrderDraft: SalesOrderDraft | null;
  /** Whether extraction metadata is loading */
  isExtractionLoading: boolean;
  /** Whether sales order draft is loading */
  isDraftLoading: boolean;
  /** Combined loading state */
  isLoading: boolean;
  /** Extraction metadata error */
  extractionError: Error | null;
  /** Sales order draft error */
  draftError: Error | null;
  /** Refetch extraction metadata */
  refetchExtraction: () => void;
  /** Refetch sales order draft */
  refetchDraft: () => void;
}

/**
 * Hook to fetch all data needed for the extraction viewer page.
 * Fetches both the extraction metadata (document + citations) and the
 * sales order draft data in parallel.
 */
export function useExtractionViewerData({
  salesOrderDraftId,
  enabled = true,
}: UseExtractionViewerDataParams): UseExtractionViewerDataReturn {
  // Fetch extraction metadata (document + citations) via RPC
  const extractionQuery = useQuery({
    ...orpc.accountsReceivable.salesOrderDrafts.getExtractionMetadata.queryOptions(
      {
        input: { stagedPurchaseOrderId: salesOrderDraftId },
      },
    ),
    enabled: enabled && !!salesOrderDraftId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch sales order draft data via REST API
  // This provides the draft data (line items, totals, etc.) to display
  const draftQuery = useQuery({
    queryKey: ['sales-order-draft', salesOrderDraftId],
    queryFn: async (): Promise<SalesOrderDraft> => {
      const response = await fetch(
        `${config.apiBaseUrl}/accounts-receivable/staged-purchase-orders/${salesOrderDraftId}`,
        { credentials: 'include' },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch sales order draft');
      }

      const data = await response.json();
      return data.data?.stagedPurchaseOrder;
    },
    enabled: enabled && !!salesOrderDraftId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const extractionData = extractionQuery.data;

  return {
    // Extraction metadata
    document: extractionData?.document ?? null,
    extraction: extractionData?.extraction ?? null,
    purchaseOrderIndex: extractionData?.purchaseOrderIndex ?? 0,

    // Sales order draft
    salesOrderDraft: draftQuery.data ?? null,

    // Loading states
    isExtractionLoading: extractionQuery.isLoading,
    isDraftLoading: draftQuery.isLoading,
    isLoading: extractionQuery.isLoading || draftQuery.isLoading,

    // Errors
    extractionError: extractionQuery.error,
    draftError: draftQuery.error,

    // Refetch functions
    refetchExtraction: extractionQuery.refetch,
    refetchDraft: draftQuery.refetch,
  };
}
